<template>
  <Container
    class="mx-auto mt-10 mb-30 max-w-281.5 max-[1440px]:max-w-185 max-[1024px]:max-w-154 max-[768px]:max-w-119.5 xl:mt-17.5 xl:mb-42.5 xl:w-[73.35%]"
  >
    <Transition name="fade" mode="out-in">
      <div
        v-if="isSubmitted"
        id="success-message"
        ref="successEl"
        key="success"
        class="flex min-h-75 flex-col items-center justify-center gap-5 border border-gray p-6 text-center lg:min-h-100 lg:p-10"
      >
        <p class="text-[22px]/[110%] font-medium lg:text-2.5xl/[110%]">
          {{ t('alumni.success_message') }}
        </p>
      </div>

      <form
        v-else
        key="form"
        @submit.prevent="handleSubmit"
        class="flex flex-col gap-10"
      >
        <div class="flex flex-col gap-5 lg:gap-10">
          <h2 class="text-[22px]/[110%] lg:text-2.5xl/[110%]">
            {{ t('alumni.form.title') }}
          </h2>
        </div>

        <div class="flex flex-col gap-10 lg:gap-17.5">
          <div class="flex flex-col gap-4 lg:gap-7.5">
            <template v-for="field in formFields" :key="field.id">
              <FormRadioGroup
                v-if="field.type === 'radio'"
                :id="field.id"
                :label="field.label"
                :options="field.options"
                v-model="(formData as any)[field.value] as string"
                :error="errors[field.value as keyof typeof errors]"
                class="xl:mb-20"
              />
              <FormCheckbox
                v-else-if="field.type === 'checkbox'"
                :id="field.id"
                :label="field.label"
                v-model="(formData as any)[field.value] as boolean"
                :error="errors[field.value as keyof typeof errors]"
              />
              <FormTextarea
                v-else-if="field.type === 'textarea'"
                :id="field.id"
                :label="field.label"
                v-model="(formData as any)[field.value] as string"
                :error="errors[field.value as keyof typeof errors]"
                class="xl:mb-13.5"
              />
              <FormInput
                v-else
                :id="field.id"
                :type="field.type"
                :label="field.label"
                v-model="(formData as any)[field.value] as string"
                :error="errors[field.value as keyof typeof errors]"
              />
            </template>
          </div>

          <button
            type="submit"
            class="cursor-pointer bg-black p-2.5 text-white lg:text-xl/[120%]"
          >
            {{ t('common.submit.button') }}
          </button>
        </div>
      </form>
    </Transition>
  </Container>
</template>

<script setup lang="ts">
import { z } from 'zod'

const { locale, t } = useI18n()
const { mutate } = useGqlMutation()

const isSubmitted = ref(false)
const successEl = ref<HTMLElement | null>(null)

watch(successEl, (el) => {
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
})
let timer: ReturnType<typeof setTimeout> | null = null

const initialFormData = {
  typ: '',
  first_name: '',
  last_name: '',
  institution: '',
  email: '',
  phone: '',
  message: '',
  gdpr: false,
}

const formData = reactive({ ...initialFormData })

const errors = reactive<Record<keyof typeof initialFormData, string>>({
  typ: '',
  first_name: '',
  last_name: '',
  institution: '',
  email: '',
  phone: '',
  message: '',
  gdpr: '',
})

const fieldToElementId: Record<keyof typeof initialFormData, string> = {
  typ: 'typ-group',
  first_name: 'firstName',
  last_name: 'lastName',
  institution: 'institution',
  email: 'email',
  phone: 'phone',
  message: 'message',
  gdpr: 'terms',
}

const clearErrors = () => {
  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = ''
  })
}

const resetForm = () => {
  Object.assign(formData, initialFormData)
  clearErrors()
  isSubmitted.value = false
}

onUnmounted(() => {
  if (timer) clearTimeout(timer)
})

const alumniSchema = z.object({
  typ: z.string().min(1, t('alumni.validation.select_role')),
  first_name: z
    .string()
    .min(1, t('alumni.validation.first_name_required'))
    .regex(/^[^0-9]*$/, t('alumni.validation.first_name_no_numbers')),
  last_name: z
    .string()
    .min(1, t('alumni.validation.last_name_required'))
    .regex(/^[^0-9]*$/, t('alumni.validation.last_name_no_numbers')),
  institution: z.string().optional(),
  email: z
    .string()
    .min(1, t('alumni.validation.email_required'))
    .email(t('alumni.validation.email_invalid')),
  phone: z
    .string()
    .optional()
    .refine((val) => !val || /^(\+?\d{1,4})?(\s?\d){9,12}$/.test(val), {
      message: t('alumni.validation.phone_invalid'),
    }),
  message: z.string().optional(),
  gdpr: z.literal(true, {
    errorMap: () => ({
      message: t('alumni.validation.gdpr_required'),
    }),
  }),
})

const handleSubmit = async () => {
  clearErrors()

  const result = alumniSchema.safeParse(formData)

  if (!result.success) {
    result.error.issues.forEach((issue) => {
      const field = issue.path[0] as keyof typeof errors
      if (field in errors) {
        errors[field] = issue.message
      }
    })

    const firstErrorField = result.error.issues[0]
      ?.path[0] as keyof typeof errors

    if (firstErrorField && fieldToElementId[firstErrorField]) {
      nextTick(() => {
        const targetId = fieldToElementId[firstErrorField]
        const el = document.getElementById(targetId)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    }
    return
  }

  try {
    await mutate('JoinAlumni', {
      typ: result.data.typ,
      first_name: result.data.first_name,
      last_name: result.data.last_name,
      email: result.data.email,
      gdpr: result.data.gdpr,
      language: locale.value,
    })
  } catch (error) {
    console.error('Error submitting form:', error)
    return
  }

  isSubmitted.value = true

  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    resetForm()
  }, 7000)
}

const roles = [
  { id: 'student', value: 'student', label: t('alumni.role.student') },
  { id: 'graduate', value: 'absolvent', label: t('alumni.role.graduate') },
  {
    id: 'academic-employee',
    value: 'akademicky_pracovnik',
    label: t('alumni.role.academic-employee'),
  },
  { id: 'employee', value: 'zamestnanec', label: t('alumni.role.employee') },
  {
    id: 'school-friend',
    value: 'pritel',
    label: t('alumni.role.school_friend'),
  },
]

const formFields = [
  {
    id: 'typ-group',
    type: 'radio',
    value: 'typ',
    label: t('alumni.form.role_label'),
    options: roles,
  },
  {
    id: 'firstName',
    value: 'first_name',
    label: t('alumni.form.first_name_label'),
    type: 'text',
  },
  {
    id: 'lastName',
    value: 'last_name',
    label: t('alumni.form.last_name_label'),
    type: 'text',
  },
  {
    id: 'institution',
    value: 'institution',
    label: t('alumni.form.institution_label'),
    type: 'text',
  },
  {
    id: 'email',
    value: 'email',
    label: t('alumni.form.email_label'),
    type: 'email',
  },
  {
    id: 'phone',
    value: 'phone',
    label: t('alumni.form.phone_label'),
    type: 'tel',
  },
  {
    id: 'message',
    value: 'message',
    label: t('alumni.form.message_label'),
    type: 'textarea',
  },
  {
    id: 'terms',
    type: 'checkbox',
    value: 'gdpr',
    label: t('alumni.form.gdpr_label'),
  },
] as any
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
